import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Chip
} from '@mui/material';
import {
    Refresh as RefreshIcon
} from '@mui/icons-material';
import * as http from '../../common/http';
import { APIUrl } from '../../common/apiConfig';

interface BranchPerformanceData {
    branch_id: number;
    branch_name: string;
    total_students: number;
    active_students: number;
    attendance_percentage: number;
    fee_collection_percentage: number;
    pending_fees: number;
}

type SortOrder = 'asc' | 'desc';
type SortColumn = 'branch_name' | 'total_students' | 'active_students' | 
                  'attendance_percentage' | 'fee_collection_percentage' | 'pending_fees';

const BranchPerformance: React.FC = () => {
    const [data, setData] = useState<BranchPerformanceData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [sortColumn, setSortColumn] = useState<SortColumn>('branch_name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const url = `${APIUrl}dashboard-coaching/branch-performance`;
            
            const response = await http.get(url);
            if (response && response.status === 'success') {
                setData(response.result || []);
                setLastRefresh(new Date());
            } else {
                setError('Failed to fetch branch performance data');
            }
        } catch (err) {
            console.error('Error fetching branch performance:', err);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, []);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const getPercentageColor = (percentage: number) => {
        if (percentage >= 90) return '#2e7d32'; // Green
        if (percentage >= 75) return '#ed6c02'; // Orange
        return '#d32f2f'; // Red
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const sortedData = [...data].sort((a, b) => {
        let aValue: any = a[sortColumn];
        let bValue: any = b[sortColumn];

        if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (sortOrder === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    if (loading && data.length === 0) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                        Branch Performance
                    </Typography>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchData} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {data.length > 0 ? (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortColumn === 'branch_name'}
                                            direction={sortColumn === 'branch_name' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('branch_name')}
                                        >
                                            <strong>Branch</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sortColumn === 'total_students'}
                                            direction={sortColumn === 'total_students' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('total_students')}
                                        >
                                            <strong>Students</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sortColumn === 'active_students'}
                                            direction={sortColumn === 'active_students' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('active_students')}
                                        >
                                            <strong>Active</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sortColumn === 'attendance_percentage'}
                                            direction={sortColumn === 'attendance_percentage' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('attendance_percentage')}
                                        >
                                            <strong>Attendance</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sortColumn === 'fee_collection_percentage'}
                                            direction={sortColumn === 'fee_collection_percentage' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('fee_collection_percentage')}
                                        >
                                            <strong>Fee Collection</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sortColumn === 'pending_fees'}
                                            direction={sortColumn === 'pending_fees' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('pending_fees')}
                                        >
                                            <strong>Pending Fees</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedData.map((branch) => (
                                    <TableRow key={branch.branch_id} hover>
                                        <TableCell>{branch.branch_name}</TableCell>
                                        <TableCell align="right">{branch.total_students}</TableCell>
                                        <TableCell align="right">{branch.active_students}</TableCell>
                                        <TableCell align="right">
                                            <Chip 
                                                label={`${branch.attendance_percentage}%`}
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: getPercentageColor(branch.attendance_percentage),
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip 
                                                label={`${branch.fee_collection_percentage}%`}
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: getPercentageColor(branch.fee_collection_percentage),
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography 
                                                variant="body2" 
                                                color={branch.pending_fees > 50000 ? 'error' : 'text.secondary'}
                                                fontWeight={branch.pending_fees > 50000 ? 'bold' : 'normal'}
                                            >
                                                {formatCurrency(branch.pending_fees)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Alert severity="info">No branch performance data available</Alert>
                )}

                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    Last updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default BranchPerformance;
